import Vue from 'vue';
import { Dispatch, GetterTree } from 'vuex';

import { Zone } from '@/precision-farming/application-maps/store/baseWorkflowStore/types/Zone';
import { UNIT_ID_GRAINS_PER_M2, UNIT_ID_KG_PER_HA } from '@/products/constants';
import { ACTIVITY_ROUGH_GUIDS } from '@/shared/constants';
import notNullOrUndefined from '@/shared/modules/notNullOrUndefinedFilter';
import { RootState } from '@/store/types';

import baseWorkflowStore from '../../store/baseWorkflowStore';
import { Getters as BaseWorkflowGetters } from '../../store/baseWorkflowStore/getters';
import { Feature } from '../../store/baseWorkflowStore/types/Heatmap';
import { WorkflowKeyForRoutes } from '../../types';
import {
  applyLossCompensation,
  calcSeedRateOfZone,
  calcVarietyValueOfZone,
  getManualSeedRateOfZone,
} from './getterUtils';
import { ApplicationMapsSeedingSoyState, Calculation, SeedingZone, WorkflowVarieties, ZoneDosage } from './types';

export type Getters = BaseWorkflowGetters & {
  paginationNextDisabledReason: string;
  varieties: WorkflowVarieties;
  calculation: Calculation;
  features: Feature[];
  zones: SeedingZone[];
  zonesByHeatmap: Zone[];
  zoneDosage: ZoneDosage[];
  zonesMinSeedrate: number;
  zonesMaxSeedrate: number;
  taskDataAsync: (dispatch: Dispatch) => Promise<unknown>;
};

const moduleGetters: GetterTree<ApplicationMapsSeedingSoyState, RootState> = {
  ...baseWorkflowStore.getters,
  paginationNextEnabled: (state) => {
    if (state.selectedFields.length === 0) {
      return false;
    }
    if (state.paginationStep <= 1) {
      return true;
    }
    if (state.paginationStep === 2 && !state.heatmaps.fetching && state.calculation.variety.id) {
      return true;
    }

    if (state.paginationStep === 3 && state.stepsCompleted) {
      return true;
    }

    return false;
  },
  paginationNextDisabledReason: (state, getters) => {
    if (getters.paginationNextEnabled) {
      return null;
    }

    switch (state.paginationStep) {
      case 1:
        return Vue.i18n.translate('Bitte w\u00e4hle ein Feld aus, bevor du fortfährst.');
      case 2:
        return Vue.i18n.translate('Bitte w\u00e4hle eine Sorte aus, bevor du fortfährst.');
      default:
        return null;
    }
  },
  varieties: (state) => state.varieties,
  calculation: (state) => state.calculation,
  features(state) {
    let features: Feature[] = [];
    Object.values(state.heatmaps.current).forEach((heatmap) => {
      features = [...features, ...heatmap.features];
    });
    return features;
  },
  zones: (state, getters: Getters) =>
    getters.zonesByHeatmaps.map((zone: Zone) => {
      const varietyValue = calcVarietyValueOfZone(zone, state.calculation);
      const seedRate = calcSeedRateOfZone(varietyValue, state.calculation);
      const seedRateWithLossCompensation = applyLossCompensation(seedRate, state.calculation);
      const manualSeedRate = getManualSeedRateOfZone(zone.name, state.calculation);

      return {
        ...zone,
        varietyValue,
        seedRate,
        seedRateWithLossCompensation,
        manualSeedRate,
      };
    }),
  zoneDosage(state, getters: Getters) {
    // Determine if this is a drill variety for unit conversions
    const currentVariety = state.varieties.current.find((v) => v.id === state.calculation.variety.id);
    const isDrillVariety = currentVariety?.seedingMethod === 'Drill';

    return getters.zones.map((zone) => {
      let dosage = zone.manualSeedRate ?? zone.seedRateWithLossCompensation;

      if (dosage) {
        if (isDrillVariety) {
          // Convert from Kö/ha to kg/ha: (Kö/ha / 10000) * thousand-grain weight / 100
          const seedRateGrainsPerSqM = dosage / 10000;
          dosage = Math.round((seedRateGrainsPerSqM * state.calculation.thousandGrainWeight) / 100);
        } else {
          // Convert from Kö/ha to Kö/m²
          dosage = Math.round(dosage / 10000);
        }
      }

      return {
        color: zone.color,
        name: zone.name,
        dosage,
      };
    });
  },
  zonesMinSeedrate: (state, getters: Getters) => {
    const seedRates = getters.zones.map((zone) => zone.seedRate).filter(notNullOrUndefined);
    return Math.min(...seedRates);
  },
  zonesMaxSeedrate: (state, getters: Getters) => {
    const seedRates = getters.zones.map((zone) => zone.seedRate).filter(notNullOrUndefined);
    return Math.max(...seedRates);
  },
  taskDataAsync: (state, getters, rootState, rootGetters) => async (dispatch: Dispatch) => {
    // TODO taskDataAsync is somehow redundant with state.task -> remove this method and adapt and use state.task
    await dispatch('activityTypes/subscribe', {}, { root: true });

    const { currentHeatmaps, zoneDosage, selectedTaskDate } = getters;
    const {
      zoneGenerationMode,
      selectedFields,
      selectedHeatmapDbId,
      selectedHeatmapTimestamp,
      selectedQuantisationCode,
      calculation,
      task,
      selectedCompany,
      varieties,
    } = state;

    const companyId = selectedCompany.id ? selectedCompany.id : rootState.auth.currentCompanies[0].id;

    // Determine if this is a drill variety for unit conversions
    const currentVariety = varieties.current.find((v) => v.id === calculation.variety.id);
    const isDrillVariety = currentVariety?.seedingMethod === 'Drill';

    const materials = [];
    if (
      calculation.material.id != null &&
      calculation.material.id !== 'manual' &&
      typeof calculation.averageDosage === 'number' &&
      calculation.averageDosage > 0
    ) {
      let workingMeansAmount = calculation.averageDosage;
      let workingMeansUnitId = calculation.unit;

      if (isDrillVariety) {
        // Convert from Kö/ha to kg/ha: Kö/m² * thousand-grain weight / 100
        const seedRateGrainsPerSqM = calculation.averageDosage / 10000;
        workingMeansAmount = Math.round((seedRateGrainsPerSqM * calculation.thousandGrainWeight) / 100);
        workingMeansUnitId = UNIT_ID_KG_PER_HA;
      } else {
        // Convert from Kö/ha to Kö/m²
        workingMeansAmount = Math.round(calculation.averageDosage / 10000);
        workingMeansUnitId = UNIT_ID_GRAINS_PER_M2; // Kö/m²
      }

      materials.push({
        workingMeanId: calculation.material.id,
        amount: workingMeansAmount,
        unitId: workingMeansUnitId,
      });
    }

    const processOrder = await rootGetters['auth/processOrderByCompanyIdAndNameAndTypeAsync'](
      rootState.auth.currentCompanies[0].id,
      rootState.auth.currentProcessOrderName,
    );

    const timeStartDate = selectedTaskDate || new Date();
    const timeStart = Math.floor(timeStartDate.getTime() / 1000);

    function appendToTaskComment(comment: string) {
      let userComment = '';
      if (task?.userComment) {
        userComment = `${task.userComment} `;
      }
      userComment = `${userComment}${comment}`;
      return userComment;
    }
    const userComment = appendToTaskComment(calculation.variety.name);

    const taskData = {
      version: '2.0',
      data: {
        companyId,
        processOrderId: processOrder.id,
        activityId: rootGetters['activityTypes/byRoughAndFineId'](ACTIVITY_ROUGH_GUIDS.SEED_MAIN).id,
        timeStart,
        state: 'new',
        fields: selectedFields.map((guid) => ({
          fieldId: guid,
          processedArea: rootGetters.fields[guid].fieldSize,
        })),
        workingMeans: materials,
        applicationMap: {
          additionalData: {
            fields: selectedFields.map((guid) => {
              const field = {
                id: guid,
                geoJsonId: Object.keys(currentHeatmaps).find((heatmapId) => heatmapId.startsWith(guid)) || null,
              };

              return field;
            }),
            zoneGenerationMode,
            selectedIndexType: 'RATE_SEED_MAIZE',
            selectedHeatmapDbId,
            selectedHeatmapTimestamp,
            selectedQuantisationCode,
            calculation: {
              ...calculation,
              unit: isDrillVariety
                ? UNIT_ID_KG_PER_HA // kg/ha
                : UNIT_ID_GRAINS_PER_M2, // Kö/m²
            },
            zoneDosage,
          },
          geoJson: currentHeatmaps,
          companyId,
          workflowKey: WorkflowKeyForRoutes.SEEDING_SOY,
        },
        userComment,
      },
    };
    return taskData;
  },
};

export default moduleGetters;
